import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  IconButton,
  TableContainer,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import { DetalhePagamento } from "../../hooks/pagamento/usePedido";
import { formatBRL, isRs } from "../../utils/currency";
import { HiArrowUturnLeft, HiTicket } from "react-icons/hi2";
import { useMutationCancelarBilhete } from "../../hooks/cancelar/mutationCancelarBilhete";
import { queryClient } from "../../libs/queryClient";
import { Loading } from "../../components/Loading";
import { MdDownload } from "react-icons/md";
import { useParams } from "react-router-dom";
import { IoTicketSharp } from "react-icons/io5";
import { Bilhete, BilheteStatusCode, DocumentoPreferencial } from "../../utils/interfaces";
import { abrirCartaoPdf, abrirVoucherPdf } from "../../libs/axios";
import { deveBloquearDevolucaoPorEmbarque } from "../../utils/devolucao";
import { shouldHideFeeDetails, useShouldShowProvider } from "../../libs/auth";
import { ModalConfirmarDevolucao } from "./ModalConfirmarDevolucao";
import { formatProvider } from "../../utils/provider";

interface Props {
  detalhe: DetalhePagamento;
  contexto: "ida" | "volta";
  isDetail?: boolean;
  showCancel?: boolean;
  agenciaRepassaMulta?: boolean;
  multaNaFatura?: boolean;
}

export const StatusPedidoLabel = {
  1: 'Aberto',
  2: 'Reservado',
  3: 'Finalizado',
  4: 'Cancelado',
  5: 'Estorno em Processamento',
  7: 'Devolvido',
  8: 'Devolvido Parcialmente',
}

export const StatusBilheteLabel = {
  1: 'Reservado',
  2: 'Confirmado',
  3: 'Cancelado',
  4: 'Expirado',
  5: 'Invalido',
  6: 'Devolvido',
  7: 'Estorno em Processamento'
}

export function DetalhesBilhete({
  detalhe,
  contexto,
  showCancel = true,
  agenciaRepassaMulta = false,
  multaNaFatura = false,
}: Props) {
  const mutation = useMutationCancelarBilhete();
  const { id } = useParams() as { id: string };
  const hideFeeDetails = shouldHideFeeDetails();
  const modalCancelamento = useDisclosure();
  const [bilheteSelecionado, setBilheteSelecionado] = useState<Bilhete | null>(null);
  const showProvider = useShouldShowProvider();
  const { bilhetes, detalhes, pagamento } = detalhe;
  const bilheteComTaxaCancelamento = bilhetes.find((bilhete) => {
    const taxa = Number(bilhete.multa ?? bilhete.multa_cancelamento ?? 0);

    return agenciaRepassaMulta
      && bilhete.status === BilheteStatusCode.Devolvido
      && Number.isFinite(taxa)
      && taxa > 0;
  });
  const taxaCancelamentoRetidaPorBilhete = Number(
    bilheteComTaxaCancelamento?.multa ?? bilheteComTaxaCancelamento?.multa_cancelamento ?? 0
  );
  const valorSeguro = Number(pagamento.valor_seguro ?? 0);
  const taxaViagemPremiadaOuSeguro = valorSeguro > 0
    ? {
        label: "Seguro",
        valor: valorSeguro,
      }
    : {
        label: "Viagem premiada",
        valor: pagamento.viagem_premiada ?? 0,
      };
  const formatTransacao = (value?: string) => {
    if (!value) {
      return "";
    }
    return value.length > 50 ? `${value.slice(0, 50)}...` : value;
  };

  const onCancelar = (bilhete: Bilhete) => {
    setBilheteSelecionado(bilhete);
    modalCancelamento.onOpen();
  };

  const confirmarCancelamento = async () => {
    if (!bilheteSelecionado) {
      return;
    }

    try {
      await mutation.mutateAsync({
        id: bilheteSelecionado.id,
        multa: Number.isFinite(multaBilheteSelecionado) ? multaBilheteSelecionado : 0,
        multaNaFatura,
      });

      queryClient.invalidateQueries({ queryKey: ["pedido"], exact: false });
      modalCancelamento.onClose();
      setBilheteSelecionado(null);
    } catch (error) {
      console.error(error);
    }
  };

  const multaBilheteSelecionado = Number(
    bilheteSelecionado?.multa ?? bilheteSelecionado?.multa_cancelamento ?? 0
  );
  const bilheteSelecionadoGpApi = String(
    bilheteSelecionado?.adapter ?? bilheteSelecionado?.adaptador ?? ""
  ).toUpperCase() === "GP-API";
  const deveExibirAvisoTaxa = agenciaRepassaMulta && bilheteSelecionadoGpApi;
  const possuiDevolucaoAnterior = bilhetes.some((bilhete) =>
    bilhete.id !== bilheteSelecionado?.id
    && [
      BilheteStatusCode.Devolvido,
      BilheteStatusCode.EstornoPendente,
    ].includes(bilhete.status as BilheteStatusCode)
  );
  const descricaoModalDevolucao = [
    ...(deveExibirAvisoTaxa
      ? [
        "Esta devolução tem uma taxa de cancelamento, definido pela viação.",
        "Após a devolução verifique a conta corrente para confirmar o valor.",
      ]
      : ["Deseja continuar com a devolução deste bilhete?"]),
    ...(possuiDevolucaoAnterior
      ? ["Aguarde um minuto entre as devoluções para conseguir realizar a próxima solicitação."]
      : []),
  ];

  return (
    <Flex flexDir={"column"} w={"full"}>
      {mutation.isPending && <Loading />}

      <Flex width={"full"} flexDir={{ xs: "column", lg: "row" }}>
        <Box px={2} w={"full"}>
          <Text as={"h4"} textTransform={"uppercase"} fontWeight={"bold"}>
            Detalhes da viagem de {contexto}
          </Text>
          {showProvider && (
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>Provedor</Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>{formatProvider(bilhetes[0]?.adaptador, bilhetes[0]?.provedorWebviagem)}</Text>
          </Flex>
          )}
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Empresa
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.empresa}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Origem
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.origem}
            </Text>
          </Flex>
          {detalhes.endereco_embarque && (
            <Flex
              justifyContent={"space-between"}
              fontWeight={"700"}
              borderBottom={"1px solid #ddd"}
              gap={4}
            >
              <Text color={"#3f3f3f"} fontSize={"sm"} whiteSpace={"nowrap"}>
                Endereço de embarque
              </Text>
              <Text color={"#3f3f3f"} fontSize={"sm"} textAlign={"right"}>
                {detalhes.endereco_embarque}
              </Text>
            </Flex>
          )}
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Destino
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.destino}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Classe
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.classe}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Saída
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.saida}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Chegada Prevista
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {detalhes.chegada}
            </Text>
          </Flex>
          
        </Box>
        {!hideFeeDetails && (
        <Box px={2} w={"full"}>
          <Text as={"h4"} textTransform={"uppercase"} fontWeight={"bold"}>
            Taxas por Bilhete
          </Text>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Preço da Passagem
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {formatBRL(pagamento.valor_bilhete)}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {taxaViagemPremiadaOuSeguro.label}
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {formatBRL(taxaViagemPremiadaOuSeguro.valor)}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Conv. Parceiro
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {formatBRL(pagamento.taxa_agencia)}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              Conveniência
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {formatBRL(pagamento.taxa_sistema)}
            </Text>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            fontWeight={"700"}
            borderBottom={"1px solid #ddd"}
          >
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              TOTAL
            </Text>
            <Text color={"#3f3f3f"} fontSize={"sm"}>
              {formatBRL(pagamento.valor_total)}
            </Text>
          </Flex>
          {taxaCancelamentoRetidaPorBilhete > 0 && (
            <Flex
              justifyContent={"space-between"}
              fontWeight={"700"}
              borderBottom={"1px solid #ddd"}
            >
              <Text color={"red.500"} fontSize={"sm"} textTransform={"uppercase"}>
                Taxa de cancelamento retida
              </Text>
              <Text color={"red.500"} fontSize={"sm"}>
                {formatBRL(taxaCancelamentoRetidaPorBilhete)}
              </Text>
            </Flex>
          )}
        </Box>
        )}
      </Flex>
      {detalhes.alerta !== null && (
      <Box py={2}>
            <Alert status='error'>
              <AlertIcon />
              <AlertTitle>{detalhes.alerta.title}</AlertTitle>
              <AlertDescription>{detalhes.alerta.mensagem}</AlertDescription>
            </Alert>
          </Box>
          )}

      {isRs(detalhes.origem, detalhes.destino) && (
        <Box py={2}>
        <Alert status='error'>
            <AlertIcon />
            <AlertTitle>Atenção</AlertTitle>
            <AlertDescription>
            Devido a essa viagem ser uma viagem intermunicipal dentro do estado do Rio Grande do Sul, é obrigatório a impressão do BPE, bilhete de passagem eletrônico
            </AlertDescription>
          </Alert>
        </Box>
      )}
          
      <Box py={4} px={2}>
        <Text fontWeight={"bold"} color={"#4c4c4c"}>
          ASSENTOS ESCOLHIDOS
        </Text>
        <TableContainer>
          <Table variant="simple" textTransform={"uppercase"}>
            <Thead>
              <Tr>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Poltrona
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Nome
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Documento
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Telefone
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Transação
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Localizador
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Serviço
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Status
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Valor
                </Th>
                <Th textAlign={"center"} bg={"#cccccc"} p={1}>
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody color={"#575757"}>
              {bilhetes.map((bilhete) => {
                const bloqueioDevolucaoBilhete = deveBloquearDevolucaoPorEmbarque(
                  bilhete.embarque,
                  bilhete.devolucao_bloqueada
                );

                return (
                <Tr
                  p={"2px 5px"}
                  key={`${bilhete.id}-bilhetes`}
                  color={
                    bilhete.status === BilheteStatusCode.Cancelado ? "red.500" :
                    bilhete.status === BilheteStatusCode.EstornoPendente ? "yellow.600" :
                    bilhete.status === BilheteStatusCode.Devolvido ? "red.500" : ""
                  }
                >
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {bilhete.assento}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {bilhete.passageiro.nome}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {bilhete.passageiro.documento}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {bilhete.passageiro.telefone}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    <Tooltip label={bilhete.id_transacao} fontSize="lg">
                      {formatTransacao(bilhete.id_transacao)}
                    </Tooltip>
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {bilhete.localizador}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    <Tooltip label={bilhete.servico} fontSize="lg">
                      {bilhete.servico.slice(0, 10)}
                    </Tooltip>
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                    textTransform={'uppercase'}
                  >
                    {StatusBilheteLabel[bilhete.status as keyof typeof StatusBilheteLabel] || ''}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {formatBRL(bilhete.valor_total)}
                  </Td>
                  <Td
                    fontSize={"sm"}
                    border={"1px solid #cccccc"}
                    textAlign={"center"}
                    px={"2px"}
                    py={"5px"}
                  >
                    {showCancel
                      && (bilhete.status !== BilheteStatusCode.Cancelado
                        && bilhete.status !== BilheteStatusCode.EstornoPendente
                        && bilhete.status !== BilheteStatusCode.Devolvido
                        && !bilhete.devolucao_presencial
                        && !bloqueioDevolucaoBilhete) && (
                        <Tooltip label="Devolver bilhete">
                          <IconButton
                            onClick={() => onCancelar(bilhete)}
                            icon={<HiArrowUturnLeft />}
                            aria-label="devolver"
                            variant={"outline"}
                            colorScheme="red"
                          />
                        </Tooltip>
                      )
                    }
                    {![
                      BilheteStatusCode.Reservado,
                      BilheteStatusCode.Expirado,
                      BilheteStatusCode.Invalido,
                      BilheteStatusCode.EstornoPendente,
                    ].includes(bilhete.status as BilheteStatusCode) && (
                      <>
                        {isRs(bilhete.cidade_origem.nome, bilhete.cidade_destino.nome) ? (
                          <Tooltip label="BPE">
                            <IconButton
                              ml={1}
                              as={"a"}
                              target="_blank"
                              href={bilhete.documento_embarque}
                              icon={<IoTicketSharp />}
                              aria-label="cancelar"
                              variant={"outline"}
                              colorScheme="green"
                            />
                          </Tooltip>
                        ) : (
                          <>
                            {bilhete.viacao.documento_preferencial.includes(DocumentoPreferencial.CartaoEmbarque) && (
                              <Tooltip label="Cartão de Embarque">
                                <IconButton
                                  ml={1}
                                  onClick={() => abrirCartaoPdf(Number(bilhete.id))}
                                  icon={<HiTicket />}
                                  aria-label="cancelar"
                                  variant={"outline"}
                                  colorScheme="green"
                                />
                              </Tooltip>
                            )}
                            {bilhete.viacao.documento_preferencial.includes(DocumentoPreferencial.BPe) && (
                              <Tooltip label="BPE">
                                <IconButton
                                  ml={1}
                                  as={"a"}
                                  target="_blank"
                                  href={bilhete.documento_embarque}
                                  icon={<IoTicketSharp />}
                                  aria-label="cancelar"
                                  variant={"outline"}
                                  colorScheme="green"
                                />
                              </Tooltip>
                            )}
                            {bilhete.viacao.documento_preferencial.includes(DocumentoPreferencial.Voucher) && (
                              <Tooltip label="Voucher">
                                <IconButton
                                  ml={1}
                                  onClick={() => abrirVoucherPdf(Number(id))}
                                  icon={<MdDownload />}
                                  aria-label="cancelar"
                                  variant={"outline"}
                                  colorScheme="green"
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Td>
                </Tr>
              )})}
            </Tbody>
            <Tfoot>
              <Tr border={"1px solid #cccccc"}>
                <Th
                  border={"1px solid #cccccc"}
                  textAlign={"right"}
                  px={"2px"}
                  py={"5px"}
                  color={"red"}
                  colSpan={9}
                >
                  TOTAL
                </Th>
                <Th
                  border={"1px solid #cccccc"}
                  textAlign={"center"}
                  px={"2px"}
                  py={"5px"}
                  color={"red"}
                >
                  {formatBRL(
                    bilhetes.reduce(
                      (accumulator, currentValue) =>
                        accumulator + currentValue.valor_total,
                      0
                    )
                  )}
                </Th>
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
      </Box>
      <ModalConfirmarDevolucao
        isOpen={modalCancelamento.isOpen}
        onClose={modalCancelamento.onClose}
        onConfirm={confirmarCancelamento}
        isLoading={mutation.isPending}
        titulo="Tem certeza que deseja devolver este bilhete?"
        descricao={descricaoModalDevolucao}
        confirmLabel="Devolver bilhete"
        multa={Number.isFinite(multaBilheteSelecionado) ? multaBilheteSelecionado : 0}
        exibirMulta={deveExibirAvisoTaxa}
        multaNaFatura={multaNaFatura}
      />
    </Flex>
  );
}
